'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { ArrowRight, Check, Search, Info } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { useCreateProcessor } from '@/lib/api/processors';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { brandingConfig } from '@/config/branding';

const tagadaProcessorLogo = '/integrations/tagadapay-logo.png';
const sandboxProcessorLogo = brandingConfig.defaultIntegrationLogo ?? brandingConfig.logo;

// Import metadata from main app constants (duplicated here for now since cross-import might be tricky)
// In a real monorepo, this should be a shared package.
const PROCESSOR_METADATA = {
  stripe: {
    name: 'Stripe',
    description: "Accept payments via Stripe's global payment platform",
    logo: '/integrations/stripe-icon.svg',
    color: '#635BFF',
    lightColor: '#F6F5FF',
  },
  airwallex: {
    name: 'Airwallex',
    description: 'Accept payments globally with Airwallex payment platform',
    logo: '/integrations/airwallex-icon.svg',
    color: '#00D4AA',
    lightColor: '#F0FDFA',
  },
  checkout: {
    name: 'Checkout',
    description: `Accept payment with Checkout.com's platform`,
    logo: '/integrations/checkout-icon.svg',
    color: '#186AFF',
    lightColor: '#F6F5FF',
  },
  nmi: {
    name: 'NMI',
    description: "Process payments through NMI's payment gateway",
    logo: '/integrations/nmi-icon.png',
    color: '#2D3142',
    lightColor: '#F5F6F7',
  },
  onyx: {
    name: 'Onyx Processing',
    description: 'Accept payments through Onyx Processing',
    logo: '/integrations/nmi-icon.png',
    color: '#111827',
    lightColor: '#F3F4F6',
  },
  finix: {
    name: 'Finix',
    description: 'Payment processing platform for software platforms',
    logo: '/integrations/finix-icon.jpg',
    color: '#4F46E5',
    lightColor: '#EEF2FF',
  },
  // Platform MOR gateway. Still very much alive: the guided setup creates it
  // programmatically with an express publishable key
  // (`buildSetupTagadaPayProcessorUpdate`). Its manual form, however, asks for
  // an account id nobody hands out self-serve — which is why it is filtered
  // out of the creation grid below while keeping its name here for the
  // processors merchants already run.
  tagadapay: {
    name: 'TagadaPay',
    description: 'Accept payments through TagadaPay Gateway',
    logo: tagadaProcessorLogo,
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  'tagadapay-router': {
    name: 'TagadaPay',
    description: 'Let TagadaPay process your payments as merchant of record',
    logo: tagadaProcessorLogo,
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  sandbox: {
    name: 'Sandbox',
    description: 'Test payment processor for development and testing',
    logo: sandboxProcessorLogo,
    color: '#10B981',
    lightColor: '#F0FDF4',
  },
  mastercard: {
    name: 'Mastercard',
    description: 'Enterprise payment processing with Mastercard Gateway',
    logo: '/integrations/mastercard.svg',
    color: '#FF5F00',
    lightColor: '#FFF5F0',
  },
  trustflow: {
    name: 'Trust Flow',
    description: 'Accept payments through Trust Flow payment gateway',
    logo: '/integrations/trustflow.png',
    color: '#1E40AF',
    lightColor: '#EFF6FF',
  },
  kesspay: {
    name: 'KessPay',
    description: 'Accept payments through KessPay Gateway',
    logo: '/integrations/kesspay-icon.png',
    color: '#FF6B35',
    lightColor: '#FFF2EE',
  },
  ovri: {
    name: 'Ovri',
    description: 'Accept payments through Ovri payment gateway',
    logo: '/integrations/ovri-icon.png',
    color: '#0066CC',
    lightColor: '#E6F2FF',
  },
  ccavenue: {
    name: 'CCAvenue',
    description: 'Accept payments through CCAvenue payment gateway',
    logo: '/integrations/ccavenue-logo.png',
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  ngenius: {
    name: 'Network Ngenius',
    description: 'Accept payments across the Middle East and Africa with Network International',
    logo: '/integrations/network-ngenius-icon.png',
    color: '#0069b1',
    lightColor: '#f1f8ff',
  },
  tap: {
    name: 'Tap Payments',
    description: 'Accept payments across the Middle East with Tap payment gateway',
    logo: '/integrations/tap-icon.svg',
    color: '#00D4AA',
    lightColor: '#F0FDFA',
    isBeta: true,
  },
  authorizenet: {
    name: 'Authorize.Net',
    description: 'Accept payments through Authorize.Net payment gateway',
    logo: '/integrations/authorizenet-icon.png',
    color: '#003366',
    lightColor: '#F0F4F8',
    isBeta: true,
  },
  convesio: {
    name: 'ConvesioPay',
    description: 'Accept payments through ConvesioPay payment gateway',
    logo: '/integrations/convesiopay.svg',
    color: '#6366F1',
    lightColor: '#EEF2FF',
    isBeta: true,
  },
  payland: {
    name: 'Payland',
    description: 'Accept card and crypto payments through Payland gateway',
    logo: '/integrations/payland-icon.png',
    color: '#1A56DB',
    lightColor: '#EBF5FF',
  },
  paysafe: {
    name: 'Paysafe',
    description: 'Accept payments globally with Paysafe payment platform',
    logo: '/integrations/paysafe-icon.png',
    color: '#00A651',
    lightColor: '#F0FDF4',
  },
  bluesnap: {
    name: 'BlueSnap',
    description: 'Simplify global payments with BlueSnap payment gateway',
    logo: '/integrations/bluesnap-icon.png',
    color: '#0066CC',
    lightColor: '#E6F2FF',
  },
  worldpay: {
    name: 'Worldpay',
    description: 'Accept payments worldwide with Worldpay payment platform',
    logo: '/integrations/worldpay-icon.png',
    color: '#CC0000',
    lightColor: '#FFF0F0',
  },
  braintree: {
    name: 'Braintree',
    description: 'Accept payments with Braintree, a PayPal service',
    logo: '/integrations/braintree-icon.png',
    color: '#009CDE',
    lightColor: '#E6F7FF',
  },
  mollie: {
    name: 'Mollie',
    description: 'Accept payments across Europe with Mollie payment gateway',
    logo: '/integrations/mollie-icon.png',
    color: '#FF6640',
    lightColor: '#FFF2EF',
  },
  fiserv: {
    name: 'Fiserv',
    description: 'Enterprise payment processing with Fiserv Commerce Hub',
    logo: '/integrations/fiserv-icon.png',
    color: '#FF6600',
    lightColor: '#FFF3E6',
  },
  paystack: {
    name: 'Paystack',
    description: 'Accept payments across Africa with Paystack payment gateway',
    logo: '/integrations/paystack-icon.png',
    color: '#00C3F7',
    lightColor: '#E6F9FF',
  },
  payu: {
    name: 'PayU',
    description: 'Global payment processing in emerging markets with PayU',
    logo: '/integrations/payu-icon.png',
    color: '#7DC243',
    lightColor: '#F3FBEB',
  },
  razorpay: {
    name: 'Razorpay',
    description: "Accept payments in India with Razorpay's payment platform",
    logo: '/integrations/razorpay-icon.png',
    color: '#3395FF',
    lightColor: '#EBF4FF',
  },
  payone: {
    name: 'PAYONE',
    description: 'Accept payments across Europe with PAYONE payment gateway',
    logo: '/integrations/payone-icon.png',
    color: '#FF3300',
    lightColor: '#FFF0EE',
  },
  globalpay: {
    name: 'Global Payments',
    description: 'Enterprise-grade payment processing with Global Payments',
    logo: '/integrations/globalpay-icon.png',
    color: '#0033A0',
    lightColor: '#EEF2FF',
  },
  buzipay: {
    name: 'BuziPay',
    description: 'Accept card payments directly through BuziPay S2S API',
    logo: '/integrations/buzipay-icon.png',
    color: '#1A56DB',
    lightColor: '#EBF5FF',
  },
  netvalve: {
    name: 'Corepay (NetValve)',
    description:
      "Accept card payments through NetValve, Corepay's gateway. On NMI or Authorize.net with Corepay? Use those connectors.",
    logo: '/integrations/netvalve-icon.svg',
    color: '#0F766E',
    lightColor: '#F0FDFA',
  },
  xoala: {
    name: 'Xoala',
    description: 'Accept card payments through Xoala, with 3-D Secure run by Xoala and card-on-file rebills',
    logo: '/integrations/xoala-icon.svg',
    color: '#4338CA',
    lightColor: '#EEF2FF',
  },
  unlimit: {
    name: 'Unlimit',
    description: 'Accept card payments through Unlimit (formerly Cardpay), with your own Unlimit merchant account.',
    logo: '/integrations/unlimit-icon.svg',
    color: '#4338CA',
    lightColor: '#EEF2FF',
  },
  routesense: {
    name: 'RouteSense',
    description: 'Route transactions through the RouteSense intelligent router',
    logo: '/integrations/routesense-icon.svg',
    color: '#0EA5E9',
    lightColor: '#F0F9FF',
    isBeta: true,
  },
};

/**
 * Types a merchant may create FROM THIS GRID. `tagadapay` is deliberately
 * absent: it sat next to `tagadapay-router` with the same logo and an almost
 * identical description, so merchants picked one at random — and the manual
 * `tagadapay` form asks for an account id that self-serve merchants never
 * receive. The guided setup still creates that type programmatically (with a
 * platform express key), which is unaffected by this filter.
 */
const CREATABLE_PROCESSOR_TYPES = Object.entries(PROCESSOR_METADATA).filter(
  ([value]) => value !== 'tagadapay',
);

const PROCESSOR_TYPES = CREATABLE_PROCESSOR_TYPES.map(([value, meta]) => ({
  value,
  label: meta.name,
  description: meta.description,
  logo: meta.logo,
  color: meta.color,
  lightColor: meta.lightColor,
  isBeta: (meta as any).isBeta || false,
}));

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { useTagadapayAccounts } from '@/lib/api/processors';
import { useTagadapayApply } from '@/hooks/use-tagadapay-apply';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CreateProcessorPage, PERMISSIONS.PROCESSORS_MANAGE);

function CreateProcessorPage() {
  const { t } = useTranslation('processors');
  const { orgRoutes, navigate } = useOrgNavigate();
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const createProcessorMutation = useCreateProcessor();

  // TagadaPay is the one processor a merchant cannot simply configure: it has
  // to be applied for first. Creating the processor before that left an
  // enabled, unusable row behind and only revealed the missing account on the
  // NEXT screen — so we check here and send them to the application instead.
  const { data: tagadapayAccounts } = useTagadapayAccounts();
  const tagadapayApply = useTagadapayApply();
  const needsTagadapayApplication =
    selectedType === 'tagadapay-router' && (tagadapayAccounts?.length ?? 0) === 0;

  const filteredProcessors = PROCESSOR_TYPES.filter((processor) =>
    processor.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const redirectToEdit = (type: string, processorId: string) => {
    switch (type) {
      case 'stripe':
        return navigate(orgRoutes.PROCESSOR_EDIT_STRIPE(processorId));
      case 'airwallex':
        return navigate(orgRoutes.PROCESSOR_EDIT_AIRWALLEX(processorId));
      case 'checkout':
        return navigate(orgRoutes.PROCESSOR_EDIT_CHECKOUT(processorId));
      case 'nmi':
        return navigate(orgRoutes.PROCESSOR_EDIT_NMI(processorId));
      case 'onyx':
        return navigate(orgRoutes.PROCESSOR_EDIT_ONYX(processorId));
      case 'routesense':
        return navigate(orgRoutes.PROCESSOR_EDIT_ROUTESENSE(processorId));
      case 'finix':
        return navigate(orgRoutes.PROCESSOR_EDIT_FINIX(processorId));
      case 'tagadapay':
        return navigate(orgRoutes.PROCESSOR_EDIT_TAGADAPAY(processorId));
      case 'tagadapay-router':
        return navigate(orgRoutes.PROCESSOR_EDIT_TAGADAPAY_ROUTER(processorId));
      case 'efipayments':
        return navigate(orgRoutes.PROCESSOR_EDIT_EFIPAYMENTS(processorId));
      case 'bakolapay':
        return navigate(orgRoutes.PROCESSOR_EDIT_BAKOLAPAY(processorId));
      case 'sandbox':
        return navigate(orgRoutes.PROCESSOR_EDIT_SANDBOX(processorId));
      case 'mastercard':
        return navigate(orgRoutes.PROCESSOR_EDIT_MASTERCARD(processorId));
      case 'trustflow':
        return navigate(orgRoutes.PROCESSOR_EDIT_TRUSTFLOW(processorId));
      case 'kesspay':
        return navigate(orgRoutes.PROCESSOR_EDIT_KESSPAY(processorId));
      case 'ovri':
        return navigate(orgRoutes.PROCESSOR_EDIT_OVRI(processorId));
      case 'ccavenue':
        return navigate(orgRoutes.PROCESSOR_EDIT_CCAVENUE(processorId));
      case 'ngenius':
        return navigate(orgRoutes.PROCESSOR_EDIT_NGENIUS(processorId));
      case 'tap':
        return navigate(orgRoutes.PROCESSOR_EDIT_TAP(processorId));
      case 'authorizenet':
        return navigate(orgRoutes.PROCESSOR_EDIT_AUTHORIZENET(processorId));
      case 'convesio':
        return navigate(orgRoutes.PROCESSOR_EDIT_CONVESIO(processorId));
      case 'adyen':
        return navigate(orgRoutes.PROCESSOR_EDIT_ADYEN(processorId));
      case 'payland':
        return navigate(orgRoutes.PROCESSOR_EDIT_PAYLAND(processorId));
      case 'paysafe':
        return navigate(orgRoutes.PROCESSOR_EDIT_PAYSAFE(processorId));
      case 'bluesnap':
        return navigate(orgRoutes.PROCESSOR_EDIT_BLUESNAP(processorId));
      case 'worldpay':
        return navigate(orgRoutes.PROCESSOR_EDIT_WORLDPAY(processorId));
      case 'braintree':
        return navigate(orgRoutes.PROCESSOR_EDIT_BRAINTREE(processorId));
      case 'mollie':
        return navigate(orgRoutes.PROCESSOR_EDIT_MOLLIE(processorId));
      case 'fiserv':
        return navigate(orgRoutes.PROCESSOR_EDIT_FISERV(processorId));
      case 'paystack':
        return navigate(orgRoutes.PROCESSOR_EDIT_PAYSTACK(processorId));
      case 'payu':
        return navigate(orgRoutes.PROCESSOR_EDIT_PAYU(processorId));
      case 'razorpay':
        return navigate(orgRoutes.PROCESSOR_EDIT_RAZORPAY(processorId));
      case 'payone':
        return navigate(orgRoutes.PROCESSOR_EDIT_PAYONE(processorId));
      case 'globalpay':
        return navigate(orgRoutes.PROCESSOR_EDIT_GLOBALPAY(processorId));
      case 'buzipay':
        return navigate(orgRoutes.PROCESSOR_EDIT_BUZIPAY(processorId));
      case 'netvalve':
        return navigate(orgRoutes.PROCESSOR_EDIT_NETVALVE(processorId));
      case 'xoala':
        return navigate(orgRoutes.PROCESSOR_EDIT_XOALA(processorId));
      case 'unlimit':
        return navigate(orgRoutes.PROCESSOR_EDIT_UNLIMIT(processorId));
    }
  };

  const handleCreate = async () => {
    if (!selectedType) {
      toast({
        title: t('updateError'),
        description: t('selectProcessorTypeError'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createProcessorMutation.mutateAsync({
        processor: {
          name: 'New processor',
          type: selectedType,
          enabled: true,
        },
      });

      const processorId = result[0]?.processor?.id;

      if (processorId) {
        toast({
          title: t('updateSuccess'),
          description: t('processorCreated'),
        });
        redirectToEdit(selectedType, processorId);
      }
    } catch (error) {
      toast({
        title: t('updateError'),
        description: t('processorCreationFailed'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="container mx-auto max-w-6xl py-8">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <BackButton to={`${orgRoutes.PAYMENT_FLOWS()}?tab=processors`} />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('createProcessor')}</h1>
            <p className="text-muted-foreground text-sm">Select a payment provider to get started</p>
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="text-muted-foreground absolute left-2.5 top-2.5 h-4 w-4" />
          <Input
            placeholder="Search processors..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredProcessors.map((processor) => {
          const isSelected = selectedType === processor.value;
          return (
            <div
              key={processor.value}
              className={cn(
                'relative flex cursor-pointer flex-col rounded-xl border-2 p-5 transition-all hover:shadow-md',
                isSelected
                  ? 'border-primary bg-primary/5 ring-primary ring-1'
                  : 'border-border hover:border-primary/50 hover:bg-accent/50',
              )}
              onClick={() => setSelectedType(processor.value)}
            >
              <div className="mb-4 flex items-center justify-between">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-lg border bg-white p-2 shadow-sm"
                  style={{ backgroundColor: processor.lightColor }}
                >
                  <img src={processor.logo} alt={processor.label} className="h-full w-full object-contain" />
                </div>
                {isSelected && (
                  <div className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full shadow-sm">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col">
                <div className="flex items-center gap-2">
                  <h3 className="text-foreground font-semibold">{processor.label}</h3>
                  {processor.isBeta && (
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                        BETA
                      </Badge>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-amber-600 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-sm">
                              {t('betaProcessorWarning')}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
                <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">{processor.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProcessors.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-muted rounded-full p-4">
            <Search className="text-muted-foreground h-8 w-8" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">No processors found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search query to find what you're looking for.
          </p>
        </div>
      )}

      {needsTagadapayApplication && (
        <div className="border-primary/30 bg-primary/[0.04] mt-8 rounded-lg border p-4 text-sm">
          <p className="text-foreground font-medium">
            {t(`tagadapayApply.${tagadapayApply.intent}Title`)}
          </p>
          <p className="text-muted-foreground mt-1">
            {t(`tagadapayApply.${tagadapayApply.intent}Body`)}
          </p>
        </div>
      )}

      <div className="mt-8 flex flex-col-reverse justify-end gap-3 border-t pt-6 sm:flex-row">
        <Button
          variant="outline"
          onClick={() => navigate(`${orgRoutes.PAYMENT_FLOWS()}?tab=processors`)}
          size="lg"
          className="w-full sm:w-auto"
        >
          {t('cancel')}
        </Button>
        {needsTagadapayApplication ? (
          <Button asChild size="lg" className="w-full sm:w-auto">
            <a href={tagadapayApply.href} target="_blank" rel="noopener noreferrer">
              {t(`tagadapayApply.${tagadapayApply.intent}Cta`)}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={!selectedType || createProcessorMutation.isPending}
            size="lg"
            className="w-full sm:w-auto sm:min-w-[120px]"
          >
            {createProcessorMutation.isPending ? t('creating') : t('next')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
